import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';

import { TextButton } from '@components/Button/variants';
import DropdownField from '@components/Form/DropdownField';
import IconArrow from '@components/Images/IconArrow';
import { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import useEventTracking from '@hooks/useEventTracking';
import useTITVPastEvents from '@hooks/useTITVPastEvents';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { AutocompleteRecord } from '@utils/interfaces';

import Event from './Event';

interface PastEventsProps {
  networkId?: number;
}

const SORT_OPTIONS = [
  { label: '6', value: 6 },
  { label: '12', value: 12 },
  { label: '24', value: 24 },
  { label: '48', value: 48 },
];

const INITIAL_VALUES = SORT_OPTIONS[0];

const PastEvents = ({ networkId }: PastEventsProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.ArchivedStream,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const { currentPage, events, isLoading, totalPages } = useTITVPastEvents({
    networkId,
    page,
    pageSize,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} />
      </div>
    );
  }

  if (!totalPages || !currentPage) return null;

  function getVisiblePageRange(page: number, totalPages: number, maxVisible = 3) {
    if (totalPages <= maxVisible) {
      return { start: 1, end: totalPages };
    }

    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = end - maxVisible + 1;
    }

    return { start, end };
  }

  const { start, end } = getVisiblePageRange(page, totalPages);
  const visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const handleFormSubmit = (values: { show: AutocompleteRecord }) => {
    setPageSize(values.show.value);
    setPage(1);
    trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: values.show.label }));
  };

  return (
    <>
      {!isLoading && events.length > 0 && (
        <Track>
          <div className="w-full border-0 border-t border-solid border-grayscale-charcoal sm:mt-40 md:mt-48 lg:mt-56">
            <div className="mt-12 flex items-center justify-between">
              <h4 className="m-0 font-suisse-intl-mono text-overline-md sm:text-overline-lg">
                Explore episodes
              </h4>
              <Formik
                initialValues={{ show: INITIAL_VALUES }}
                enableReinitialize
                onSubmit={handleFormSubmit}
              >
                {({ setFieldValue, submitForm }) => (
                  <Form className="flex items-center gap-12">
                    <DropdownField
                      className="flex w-[115px] items-center gap-12 text-primary-black"
                      labelClassName="!text-primary-black !font-suisse-intl !text-body-lg !m-0"
                      name="show"
                      options={SORT_OPTIONS}
                      label="Show"
                      isClearable={false}
                      isSearchable={false}
                      onChange={(selectedOption: AutocompleteRecord) => {
                        setFieldValue('show', selectedOption);
                        submitForm();
                      }}
                    />
                    <span className="hidden font-suisse-intl text-body-lg text-primary-black xs:inline">
                      per page
                    </span>
                  </Form>
                )}
              </Formik>
            </div>
            <div
              className={clsx(
                'mb-16 flex w-full flex-row flex-wrap justify-center gap-x-40 gap-y-56 py-40 sm:grid sm:grid-cols-3 sm:gap-x-72 ',
                {
                  'border-0 border-b border-solid border-grayscale-beige': totalPages > 1,
                }
              )}
            >
              {events.map((event, idx) => (
                <Event key={idx} event={event} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mb-24 flex items-center justify-center gap-24">
                {start > 1 && (
                  <TextButton
                    as="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label="Previous Page"
                    className="py-1 rounded !w-min px-3 opacity-100"
                  >
                    <IconArrow className="w-24 stroke-2 text-primary-magenta" />
                  </TextButton>
                )}
                <div className="flex items-center gap-32">
                  {visiblePages.map((pageNumber) => (
                    <TextButton
                      as="button"
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className="py-1 px-3 font-suisse-intl text-button-lg no-underline hover:!text-primary-black"
                    >
                      <span
                        className={clsx({
                          underline: pageNumber === currentPage,
                          'text-grayscale-gray ': pageNumber !== currentPage,
                          'hover:text-primary-magenta dark:hover:text-primary-pink':
                            pageNumber !== currentPage,
                        })}
                      >
                        {pageNumber}
                      </span>
                    </TextButton>
                  ))}
                </div>
                {end < totalPages && (
                  <TextButton
                    as="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next Page"
                    className="py-1 rounded !w-min px-3 opacity-100"
                  >
                    <IconArrow className="py-1 w-24 rotate-180 stroke-2 text-primary-magenta" />
                  </TextButton>
                )}
              </div>
            )}
          </div>
        </Track>
      )}
    </>
  );
};

export default PastEvents;
